import React, { useEffect, useState } from 'react'
import Tabs, { ITab } from '../../components/Tabs'
import ItemsApp, { IItemModel } from '../items/ItemsApp'
import DynamicModelSelect from '../../components/Fields/DynamicModelSelect'
import IssuesApp, { IIssuesModal, issuesTypesList } from './IssuesApp'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import ItemInventory from '../items/ItemInventory'
import { Button, Skeleton } from '@mui/material'
import { useDispatch } from 'react-redux'
import { handleRequestError } from '../../services/api'
import QuantitySlider from '../transactions/QuantitySlider'
import BasicModalWindow from '../../components/BasicModalWIndow'
import DynamicForm, { IDynamicForm, IRequiredField } from '../../components/Form/DynamicForm'
import { addNewForm, createOrUpdateElementData } from '../../components/Form/formsSlice'
import GenerateForm from '../../components/Form/GenerateForm'
import { LoadingButton } from '@mui/lab'
import { IAxiosDefaultDataObject } from '../../config'
import AlertElement from '../../components/AlertElement'
import UsersApp from '../user/UsersApp'

interface ICreateNewIssueProps {
    itemId?: number,
    requestNumber?: string,
    doAfterCreate?: (issue: IIssuesModal) => void,
    customWarehouseId?: number | null,
    customSectionId?: number | null,
    customLocationId?: number | null,
    allocatedWarehouseId?: number | null,
    allocatedSectionId?: number | null,
    allocatedLocationId?: number | null,
    customQuantity?: number | null,
}

export default function CreateNewIssue(props: ICreateNewIssueProps) {

    const dispatch = useDispatch()

    const {
        itemId,
        requestNumber,
        doAfterCreate,
        allocatedWarehouseId,
        allocatedSectionId,
        allocatedLocationId,
        customWarehouseId,
        customSectionId,
        customLocationId,
        customQuantity,
    } = props

    const [item, setItem] = useState<number | null>(null)
    const [itemDetails, setItemDetails] = useState<IItemModel | null>(null)

    const [isAllocation, setIsAllocation] = useState<boolean>(false)
    const [quantity, setQuantity] = useState<number>(0)
    const [maxQuantity, setMaxQuantity] = useState<number>(0)

    const [showQuantityModal, setShowQuantityModal] = useState<boolean>(false)

    const [warehouseId, setWarehouseId] = useState<number | null>(null)
    const [sectionId, setSectionId] = useState<number | null>(null)
    const [locationId, setLocationId] = useState<number | null>(null)

    const [loading, setLoading] = useState<boolean>(false)

    const [activeTab, setActiveTab] = useState<string>("item")
    const [availableTabs, setAvailableTabs] = useState<string[]>([
        "item",
    ])

    const itemsApp = new ItemsApp()
    const issuesApp = new IssuesApp()

    const prefix = issuesApp.elemPrefix

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(prefix + name, elementsData).value

    /**
     * Handle submit 
     */
    const handleSubmit = () => {
        
        let data: IAxiosDefaultDataObject = {
            item_id: item,
            reported_by: singleElemDataValue("reported_by"),
            issue_type: singleElemDataValue("issue_type"),
            description: singleElemDataValue("description"),
            timestamp: singleElemDataValue("timestamp"),
            quantity: quantity,
            request_ref: requestNumber,
            warehouse_id: warehouseId,
            section_id: sectionId,
            location_id: locationId,
            allocated_warehouse_id: allocatedWarehouseId,
            allocated_section_id: allocatedSectionId,
            allocated_location_id: allocatedLocationId,
            caused_by: singleElemDataValue("caused_by"),
            description_text: singleElemDataValue("description_text"),
            issue_images: singleElemDataValue("issue_images"),
        }

        let requiredFields: IRequiredField[] = issuesApp.formConfig.requiredFields

        DynamicForm.resetRequiredFieldsHelperText(requiredFields, elementsData, dispatch)
        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)

        if (verifyForm.isValid){
            setLoading(true)

            issuesApp.createNewInstance(data)
                .then(response => {
                    setLoading(false)

                    if (doAfterCreate){
                        doAfterCreate(response.data)
                    }

                })
                .catch(error => {
                    setLoading(false)
                    handleRequestError(error, dispatch)
                })
        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }

    }

    /**
     * 
     */
    const detailsForm: IDynamicForm = {
        name: "issues_form",
        elements: [
                {
                    id: issuesApp.elemPrefix + "issue_type",
                    xs: 4,
                    type: "Select",
                    label: "Type",
                    fullWidth: true,
                    items: issuesTypesList
                },
                {
                    id: issuesApp.elemPrefix + "timestamp",
                    xs: 4,
                    type: "DateAndTime",
                    label: "Timestamp",
                    fullWidth: true,
                    // defaultValue: new Date()
                },
                {
                    id: issuesApp.elemPrefix + "caused_by",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Caused by",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns
                },
                // {
                //     id: issuesApp.elemPrefix + "description",
                //     xs: 12,
                //     type: "BlockEditor",
                //     label: "Description",
                //     fullWidth: true,
                // },
                {
                    id: issuesApp.elemPrefix + "description_text",
                    xs: 12,
                    type: "TextField",
                    label: "Description",
                    fullWidth: true,
                    multiline: true,
                    minRows: 4,
                },
                {
                    id: issuesApp.elemPrefix + "issue_images",
                    xs: 12,
                    type: "Images",
                    label: "Images",
                    fullWidth: true
                },
        ]
    }

    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        detailsForm.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: ""
        })))
    }

    /**
     * No tab
     */
    const returnNoTab = () => <></>

    /**
     * Select item
     */
    const renderSelectItem = () => {
        return(
            <SectionBoxContainer
                showTitle
                title="Select Item"
            >
                <DynamicModelSelect 
                    id={`${prefix}item_id`}
                    type="DynamicModelSelect"
                    xs={12}
                    handleOnChange={(id: string, value: any) => {
                        setItem(value)
                    }}
                    data={{ name: `${prefix}item_id`, value: item }}
                    fullWidth
                    appClass={itemsApp}
                    selectLabel="Select Item"
                    label="Item"
                    gridColumns={itemsApp.gridDataColumns}
                />
            </SectionBoxContainer>
        )
    }

    /**
     * Select quantity
     */
    const selectQuantity = () => {
        return(
            <SectionBoxContainer
                showTitle
                title="Select Quantity"
            >

                {itemDetails && itemDetails.is_container && <div style={{ marginBottom: 10 }}><AlertElement type="info" message="When reporting container as missing/damaged, the items inside the container will be updated automatically as well. If you don't wish that, first remove the items from it." /></div>}
                {!itemDetails ? (
                    <>
                        <Skeleton variant="rectangular" width={"100%"} height={60} style={{ marginBottom: 10 }} />
                        <Skeleton variant="rectangular" width={"100%"} height={60} style={{ marginBottom: 10 }} />
                        <Skeleton variant="rectangular" width={"100%"} height={60} style={{ marginBottom: 10 }} />
                    </>
                ) : (
                    <ItemInventory 
                        itemUniqueId={itemDetails.unique_id} 
                        displayOnlyRecords 
                        customButtons={(inventory) => {

                            let disabled: boolean = false

                            if (inventory.user_id){
                                disabled = true
                            }

                            if (inventory.quantity === 0){
                                disabled = true
                            }

                            const onClick = () => {
                                setMaxQuantity(inventory.quantity)
                                setWarehouseId(inventory.warehouse_id)
                                setSectionId(inventory.section_id)
                                setLocationId(inventory.location_id)

                                if (inventory.quantity === 1){
                                    setQuantity(inventory.quantity)
                                    setIsAllocation(true)
                                }else{
                                    setShowQuantityModal(true)
                                }
                            }

                            return(
                                <div>
                                    <Button disabled={disabled} className="general-button" color="success" variant="text" onClick={() => onClick()}>Select</Button>
                                </div>
                            )
                        }}
                    />
                )}
            </SectionBoxContainer>
        )
    }

    /**
     * Details
     */
    const renderDetails = () => {
        return(
            <SectionBoxContainer
                showTitle
                title="Issue Details"
                description={requestNumber ? "Request No.: " + requestNumber : ""}
            >
                {/* allocated warehouse {allocatedWarehouseId ? allocatedWarehouseId : "none"} allocated section {allocatedSectionId ? allocatedSectionId : "none"} */}
                <GenerateForm name={detailsForm.name} />
                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">

                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="success" variant="contained" onClick={() => handleSubmit()}>Create</LoadingButton>

                    </div>
                </div>
            </SectionBoxContainer>
        )
    }

    /**
     * Select allocations
     */

    const tabs: ITab[] = [
        {
            name: "item",
            title: "Item",
            icon: "inventory",
            component: renderSelectItem
        },
        {
            name: "quantity",
            title: "Quantity",
            icon: "info",
            component: selectQuantity
        },
        {
            name: "details",
            title: "Details",
            icon: "list",
            component: renderDetails
        },
        // {
        //     name: "final",
        //     title: "Final",
        //     icon: "done",
        //     component: returnNoTab
        // },
    ]

    const addAvailableStep = (step: string) => {
        setAvailableTabs(prev =>
          prev.includes(step) ? prev : [...prev, step]
        )
    }

    const removeAvailableStep = (step: string) => {
        setAvailableTabs(prev =>
          prev.filter(s => s !== step)
        )
    }

    /**
     * Handle close quantity modal
     */
    const handleCloseQuantityModal = () => {
        setShowQuantityModal(false)
        setMaxQuantity(0)
        setQuantity(0)

        setWarehouseId(null)
        setSectionId(null)
        setLocationId(null)
        setIsAllocation(false)
    }

    /**
     * Handle quantity selected
     */
    const handleQuantitySelected = () => {
        setIsAllocation(true)
        setShowQuantityModal(false)
    }

    /**
     * Default data
     */
    useEffect(() => {
        if (itemId){
            setItem(itemId)
            addAvailableStep("quantity")
            setActiveTab("quantity")

            /**
             * Load item
             */
            itemsApp.singleInstanceDetails(itemId)
                .then(response => {
                    setItemDetails(response.data)
                })
                .catch(error => {
                    handleRequestError(error, dispatch)
                })
        }

    }, [itemId])

    /**
     * Move to the next step
     */
    useEffect(() => {
        if (isAllocation){
            setActiveTab("details")
            addAvailableStep("details")
        }else{
            removeAvailableStep("details")
        }
    }, [isAllocation])

    useEffect(() => {

        if (customWarehouseId){
            setWarehouseId(customWarehouseId)
        }

        if (customSectionId){
            setSectionId(customSectionId)
        }

        if (customLocationId){
            setLocationId(customLocationId)
        }

        if (customQuantity){
            setQuantity(customQuantity)
            setIsAllocation(true)
        }

    }, [customWarehouseId, customSectionId, customLocationId, customQuantity])

    useEffect(() => {
        dispatch(addNewForm({
            name: detailsForm.name,
            elements: detailsForm.elements
        }))
        createFormElemData()
    }, [])

    return(
        <div className="new-issue-container">

            <BasicModalWindow
                shown={showQuantityModal}
                size="sm"
                handleClose={handleCloseQuantityModal}
                showHeader
                title="Select Quantity"
                actionButtons={[
                    {
                        label: "Done",
                        color: "success",
                        onClick: handleQuantitySelected,
                        disabled: quantity > 0 ? false : true
                    },
                    {
                        label: "Cancel",
                        color: "error",
                        onClick: handleCloseQuantityModal
                    }
                ]}
                actionButtonsBottom
            >
                <QuantitySlider itemMaxAvailableQnt={maxQuantity} quantity={quantity} updateQuantity={(qnt) => setQuantity(qnt)} />
            </BasicModalWindow>

            <Tabs 
                activeTab={activeTab}
                availableTabs={availableTabs}
                updateActivateTab={(name) => setActiveTab(name)}
                tabs={tabs}
            />
        </div>
    )
}